//Voucher
import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import TopBarHeader from "../../components/TopBarHeader";
import Uploader from "../../components/Uploader";
import Table from "./Table";
import Modal from "../../components/Modal";
import VisorPDF from "../../components/VisorPDF";
import * as XLSX from "xlsx";
import { PDFDocument } from "pdf-lib";
import { useAuth } from "../../components/AuthContext";

// Límites de archivo
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const validarTamanoArchivo = (archivo) => archivo.size <= MAX_FILE_SIZE;

const MAX_PAGES = 100; // Suponiendo que deseas limitar a 4 páginas

const validarNumeroPaginas = async (archivo) => {
  try {
    const arrayBuffer = await archivo.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const numPages = pdfDoc.getPageCount();
    return numPages <= MAX_PAGES;
  } catch (error) {
    console.error("Error al leer el archivo PDF:", error);
    return false;
  }
};

const generarIdentificadorUnico = () => {
  const fecha = new Date().toISOString();
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${fecha}-${randomStr}`;
};

function Voucher() {
  const [errorMensaje, setErrorMensaje] = useState("");
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCedula, setSelectedCedula] = useState(null);

  const { currentUser } = useAuth(); // Obtener el usuario actual del contexto de autenticación
  const userId = currentUser ? currentUser.email : null; // Verificar si el usuario está autenticado
  useEffect(() => {}, [resultados, cargando]);

  const openModal = (archivo, tipo) => {
    if (tipo === "pdf") {
      
      setSelectedCedula({ archivoURL: archivo.archivoURL });
    } else if (tipo === "imagenAFP") {
      setSelectedCedula({ imagen: archivo.imagen });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // Elimina el prefijo data URL
      reader.onerror = (error) => reject(error);
    });
  const onDrop = useCallback(async (acceptedFiles) => {
    const archivosValidos = [];

    for (const archivo of acceptedFiles) {
      const isValid = await esArchivoValido(archivo);
      if (isValid) {
        archivosValidos.push(archivo);
      }
    }

    if (archivosValidos.length > 0) {
      const archivosIniciales = archivosValidos.map((archivo) => ({
        archivoName: archivo.name,
        estado: "procesando",
        archivoOriginal: archivo, // Guardar el archivo original
        NOMBRE: "",
        MES: "",
        ANO: "",
        Asistencia: [],
      }));

      setResultados((prevResultados) => [
        ...prevResultados,
        ...archivosIniciales,
      ]);

      archivosValidos.forEach((archivo) => {
        enviarArchivo(archivo, archivo.name);
      });
    }
  }, []);

  // Función para estandarizar la fecha a dd-mm-aaaa
  const estandarizarFecha = (fecha) => {
    const [dia, mes, ano] = fecha.split("-");
    const anoFormateado = ano.length === 2 ? `20${ano}` : ano;
    return `${dia.padStart(2, "0")}-${mes.padStart(2, "0")}-${anoFormateado}`;
  };

  // Función para estandarizar la hora a hh:mm
  const estandarizarHora = (hora) => {
    if (hora.includes(".")) {
      return hora.replace(".", ":");
    }
    return hora;
  };

  const enviarArchivo = async (
    archivo,
    archivoName,
    reintento = false,
    intentos = 0
  ) => {
    const MAX_INTENTOS = 3;

    try {
      const archivoId = generarIdentificadorUnico();
      const base64 = await fileToBase64(archivo);
      const payload = {
        userId: userId, // Agrega el userId al payload
        rawDocument: {
          mimeType: archivo.type,
          content: base64,
        },
      };
      
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_STAGE}/gemini/asistencia/voucher`;
      let response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });
      

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      let data = await response.json();
      if (!data || !data.body || !data.body.Asistencia) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? {
                  ...resultado,
                  estado: "error",
                  mensajeError: "Respuesta inesperada de la API",
                }
              : resultado
          )
        );
        throw new Error("Respuesta inesperada de la API");
      }

      const blob = new Blob([archivo], { type: archivo.type });
      const archivoURL = URL.createObjectURL(blob);

      const asistencia = data.body.Asistencia.map((asistencia) => {
        const fecha = estandarizarFecha(asistencia.DIA || "N/A");
        const [dia, mes, ano] = fecha.split("-");
        return {
          CODIGO: asistencia.CODIGO || "N/A",
          NOMBRE: asistencia.NOMBRE || "N/A",
          RUT: asistencia.RUT || "N/A",
          DIA: dia,
          MES: mes,
          ANO: ano,
          HORA_ENTRADA: estandarizarHora(asistencia.HORA_ENTRADA || "N/A"),
          HORA_SALIDA: estandarizarHora(asistencia.HORA_SALIDA || "N/A"),
          OBSERVACIONES: asistencia.OBSERVACIONES || "Sin observaciones",
          HORAS_TRABAJADAS: calcularHorasTrabajadas(
            estandarizarHora(asistencia.HORA_ENTRADA || "N/A"),
            estandarizarHora(asistencia.HORA_SALIDA || "N/A")
          ),
        };
      });

      const resultadoConDatos = {
        archivoId,
        archivoName,
        estado: "Listo",
        archivoURL, // Almacenar la URL del archivo a nivel del archivo
        Asistencia: asistencia,
      };

      // Verificar si algún código es inválido
      const tieneCodigoInvalido = asistencia.some(
        (as) =>
          !as.CODIGO ||
          as.CODIGO === "N/A" ||
          as.CODIGO === null ||
          as.CODIGO === ""
      );

      if (tieneCodigoInvalido && intentos < MAX_INTENTOS) {
        throw new Error("Código inválido, reintentando...");
      }

      setResultados((prevResultados) =>
        prevResultados.map((resultado) =>
          resultado.archivoName === archivoName
            ? { ...resultado, ...resultadoConDatos }
            : resultado
        )
      );

      

      return { success: true, resultado: resultadoConDatos };
    } catch (error) {
      console.error("Error al procesar el archivo:", error);

      if (intentos < MAX_INTENTOS) {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? {
                  ...resultado,
                  estado: "reintentando",
                  mensajeError: error.message,
                }
              : resultado
          )
        );

        await new Promise((resolve) => setTimeout(resolve, 3000));
        return enviarArchivo(archivo, archivoName, true, intentos + 1);
      } else {
        setResultados((prevResultados) =>
          prevResultados.map((resultado) =>
            resultado.archivoName === archivoName
              ? { ...resultado, estado: "error", mensajeError: error.message }
              : resultado
          )
        );
        return { success: false, error: error.toString(), resultado: null };
      }
    }
  };

  // Reutilizar la función de cálculo de horas trabajadas
  const calcularHorasTrabajadas = (horaEntrada, horaSalida) => {
    if (
      horaEntrada === "AUSENTE" ||
      horaSalida === "AUSENTE" ||
      horaEntrada === "N/A" ||
      horaSalida === "N/A"
    ) {
      return "0:00";
    }

    const [horaE, minutoE] = horaEntrada.split(":").map(Number);
    const [horaS, minutoS] = horaSalida.split(":").map(Number);

    if (isNaN(horaE) || isNaN(minutoE) || isNaN(horaS) || isNaN(minutoS)) {
      return "NaN:NaN";
    }

    let horasTrabajadas = horaS - horaE;
    let minutosTrabajados = minutoS - minutoE;

    if (minutosTrabajados < 0) {
      minutosTrabajados += 60;
      horasTrabajadas -= 1;
    }

    if (horasTrabajadas < 0) {
      horasTrabajadas += 24;
    }

    return `${horasTrabajadas}:${minutosTrabajados
      .toString()
      .padStart(2, "0")}`;
  };

  // Descargar Excel
  const descargarExcel = (datos, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(
      datos
        .map((resultado) => {
          return resultado.Asistencia.map((asistencia) => ({
            "Nombre Archivo": resultado.archivoName, // Añadir nombre del archivo desde el resultado
            Código: asistencia.CODIGO,
            Nombre: asistencia.NOMBRE,
            RUT: asistencia.RUT,
            Fecha: formatearFecha(
              asistencia.DIA,
              asistencia.MES,
              asistencia.ANO
            ),
            "Hora Entrada": asistencia.HORA_ENTRADA,
            "Hora Salida": asistencia.HORA_SALIDA,
            "Horas Trabajadas": asistencia.HORAS_TRABAJADAS,
            Observaciones: asistencia.OBSERVACIONES,
          }));
        })
        .flat()
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Asistencia");

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const reintentarEnvio = (archivoName) => {
    setResultados((prevResultados) =>
      prevResultados.map((resultado) =>
        resultado.archivoName === archivoName
          ? {
              ...resultado,
              estado: "procesando",
              mensajeError: "",
              Asistencia: [],
            }
          : resultado
      )
    );

    const archivo = resultados.find(
      (resultado) => resultado.archivoName === archivoName
    );
    if (archivo && archivo.archivoOriginal) {
      enviarArchivo(archivo.archivoOriginal, archivoName, true);
    }
  };

  const formatearMes = (mes) => {
    const meses = {
      enero: "01",
      febrero: "02",
      marzo: "03",
      abril: "04",
      mayo: "05",
      junio: "06",
      julio: "07",
      agosto: "08",
      septiembre: "09",
      octubre: "10",
      noviembre: "11",
      diciembre: "12",
    };

    if (!mes) return "N/A";
    if (meses[mes.toLowerCase()]) return meses[mes.toLowerCase()];
    if (/^\d{2}$/.test(mes)) return mes;
    return "N/A";
  };

  const formatearFecha = (dia, mes, ano) => {
    const mesFormateado = formatearMes(mes);
    const diaFormateado = dia.toString().padStart(2, "0");
    const anoFormateado = ano || "N/A";

    return `${diaFormateado}-${mesFormateado}-${anoFormateado}`;
  };

  const esFechaInvalida = (dia, mes, ano) => {
    const mesFormateado = formatearMes(mes);
    return dia === "N/A" || mesFormateado === "N/A" || ano === "N/A";
  };

  const esArchivoValido = async (archivo) => {
    if (!validarTamanoArchivo(archivo)) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el tamaño máximo permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    if (archivo.type !== "application/pdf") {
      setErrorMensaje(
        `Solo se permiten archivos PDF. El archivo ${archivo.name} no es válido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    const esValido = await validarNumeroPaginas(archivo);
    if (!esValido) {
      setErrorMensaje(
        `El archivo ${archivo.name} excede el número máximo de páginas permitido.`
      );
      setTimeout(() => setErrorMensaje(""), 5000);
      return false;
    }

    return true;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: true,
  });

  return (
    <div {...getRootProps()} className="digitalizador-cedulas">
      <input {...getInputProps()} style={{ display: "none" }} />
      <TopBarHeader />
      <h1 className="h1-titulo-2">Voucher de Asistencia</h1>
      <p className="description">
        Identifica y procesa datos de voucher de asistencia utilizando IA Generativa.
      </p>

      <Uploader
        onResultadosActualizados={setResultados}
        inputProps={getInputProps}
      />
      {errorMensaje && <div className="error-mensaje">{errorMensaje}</div>}
      {resultados.length === 0 && cargando && (
        <div className="loader-container">
          <p>Procesando archivos...</p>
          <div className="loader"></div>
        </div>
      )}

      {resultados.length > 0 && (
        <>
          <button
            className="button"
            onClick={() => descargarExcel(resultados, "ResultadosAsistencia")}
            disabled={resultados.length === 0}
          >
            Descargar Excel
          </button>
          <div className="contenedor-tabla">
            {resultados.map((resultado, index) => (
              <Table
                key={index}
                resultados={resultado}
                reintentarEnvio={reintentarEnvio}
                openModal={openModal} // Pasar la función openModal aquí
              />
            ))}
          </div>
        </>
      )}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedCedula && selectedCedula.archivoURL && (
          <VisorPDF archivo={selectedCedula.archivoURL} />
        )}
        {selectedCedula && selectedCedula.imagen && (
          <img
            src={`data:image/png;base64,${selectedCedula.imagen}`}
            alt="Imagen AFP"
          />
        )}
      </Modal>

      {cargando && (
        <div className="loader-container">
          <div className="loader-content">
            <div className="loader"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Voucher;
